import {
  BrowserClock,
  BrowserId,
  createBrowserHomeNodeCandidateStore,
  createBrowserStageCache,
  createHomeNodeOwnerViewRepository,
  createLegacyAccessTokenProvider,
  createBrowserTransportManager,
  DeviceSessionClient,
  DeviceSessionRevokedError,
  HttpTransportSupervisor,
  WebRtcHomeNodeClient,
  WebRtcPromotionController,
  WebRtcSignalingWsAdapter,
  type DevicePairingChallenge,
  type HttpWsHomeNodeClientEvent,
  type KernelHostRuntime,
  type ManagedHomeNodeClient,
  type RtcIceServerLike,
  type WebRtcPromotionPort,
  discoverNodeByAddress,
  probeHomeNodeClaimed,
  probeLanCandidate,
  StorageMirroredIdentity,
  WebCryptoIdentity,
} from "@econ-v1/adapters-web";
import { createDedicatedWorkerStoragePort } from "@econ-v1/adapters-web/storage-worker";
import {
  createAgentDetailQuery,
  createAgentListQuery,
  createConversationLogSync,
  createConversationListQuery,
  createConversationMessagesQuery,
  ConversationService,
  createDiscoveryCandidatesQuery,
  createGraphDetailQuery,
  createGraphListQuery,
  createL402HistoryQuery,
  createNotificationsQuery,
  createOnboardingStatusQuery,
  createPaymentHistoryQuery,
  createSettingDetailQuery,
  createSettingsRevisionedListQuery,
  createWalletBalanceQuery,
  DiscoveryProbeService,
  DomainError,
  LanCandidateRefreshService,
  OnboardingStatusService,
  OwnerViewService,
  WalletMirrorService,
  type OwnerViewRepository,
  type QueryName,
  type SyncStrategy,
} from "@econ-v1/domain";
import {
  CapabilityAuthorization,
  CapabilityDispatcher,
  ClientTelemetryCollector,
  createEventEmittingStorageRepositories,
  createFeedTimelineQuery,
  createHomeSummaryQuery,
  createOutboxStatusQuery,
  createOperationalSummaryQuery,
  createPeerDirectoryQuery,
  createSettingsQuery,
  DiscoveryCommandDispatcher,
  discoveryInvalidation,
  NodeDiscoveryCommandDispatcher,
  DurableEventDispatcher,
  IdentityStatusService,
  KernelEventBus,
  KernelRpcServer,
  OutboxRunner,
  type OutboxDelivery,
  type KernelRpcCapabilityDispatcher,
  type KernelRpcCommandDispatcher,
  type KernelRpcLiveQueries,
  type KernelRpcStageRegistry,
  KernelScheduler,
  LiveQueryEngine,
  LiveQueryRegistry,
  SettingsCommandDispatcher,
  StageRegistryService,
  StorageRecoveryCommandDispatcher,
  SyncEngine,
  SyncStrategyRegistry,
} from "@econ-v1/kernel";
import {
  DEFAULT_CLIENT_NODE_ORIGIN,
  type ClockPort,
  type ConversationRepository,
  type HttpHomeNodeCandidate,
  type HomeNodeCandidate,
  type HomeNodeCandidateStorePort,
  type HomeNodeClient,
  type HomeNodeDialer,
  type IdentityPort,
  type NotificationRepository,
  type StageAssetPort,
  type StageRegistryPort,
  type StorageDatabasePort,
  type StorageRecoveryState,
  type StoredHomeNodeCandidate,
} from "@econ-v1/ports";
import { DISCOVER_NODE_COMMAND, DISCOVERY_PROBE_COMMAND, DISCOVERY_PROMOTE_COMMAND } from "@econ-v1/rpc";

import {
  createClientSyncOutboxDelivery,
  DEFAULT_CLIENT_SYNC_OUTBOX_ROUTES,
  type ClientSyncOutboxRoute,
} from "./client-sync-delivery.js";

export interface P0HomeNodeClient extends HomeNodeClient {
  close(): void;
  subscribe(listener: (event: HttpWsHomeNodeClientEvent) => void): () => void;
}

export interface P0StoragePort extends StorageDatabasePort {
  subscribeRecoveryState(listener: (state: StorageRecoveryState) => void): () => void;
}

export interface P0SyncDomain {
  readonly domain: string;
  readonly strategy: SyncStrategy<unknown>;
}

/**
 * Row shape of the `client-storage.state.v1` live query — the shell's honest view of
 * durable-storage availability (#1661). `code` is a `StorageRecoveryCode` and is present
 * only when `status === "recovery-required"`.
 */
export interface ClientStorageStatusRow {
  readonly code?: string;
  readonly message?: string;
  readonly status: "ready" | "recovery-required" | "starting";
}

export interface P0BackgroundSync {
  close?(): void;
  request(): void | Promise<void>;
}

export interface P0KernelRuntimeOptions {
  readonly backgroundSync?: P0BackgroundSync;
  readonly telemetry?: ClientTelemetryCollector;
  /** Explicitly ordered transport candidates; only HTTP LAN/WAN candidates are used in the PWA. */
  readonly homeNodeCandidates?: readonly HomeNodeCandidate[];
  /**
   * The lan-direct-pairing plan's C3 persistence seam — mainly for tests. Production leaves
   * this unset, which defaults to `createBrowserHomeNodeCandidateStore()`
   * (`@econ-v1/adapters-web`, `localStorage`-backed under the `client-node.home-node` key).
   * Read synchronously once, at boot, to feed BOTH `resolveInitialHomeNode` (the transport
   * supervisor's candidate set) and `resolveHomeNodeBaseUrl` (C5 — `DeviceSessionClient`'s
   * single pairing-plane base URL) below.
   */
  readonly homeNodeCandidateStore?: HomeNodeCandidateStorePort;
  /** Explicit server capability mappings for durable outbox replay. */
  readonly outboxRoutes?: readonly ClientSyncOutboxRoute[];
  readonly storageWorkerUrl?: string | URL;
  readonly transportCandidateTimeoutMs?: number;
  readonly transportRecoveryCadenceMs?: number;
  readonly transportRefreshCadenceMs?: number;
  readonly transportPriorityGraceMs?: number;
  readonly syncCadenceMs?: number;
  readonly syncDomains?: readonly P0SyncDomain[];
  /**
   * WAN WebRTC direct promotion (ticket #1865: wiring N4's
   * `WebRtcPromotionController` + N5's ladder policy into this composition
   * root). Overrides the `?webrtc=1` URL dev-flag default (see
   * `isWanWebRtcPromotionEnabled` below) — mainly for tests. Leave unset in
   * production; the flag is what gates rollout there.
   */
  readonly webrtcPromotionEnabled?: boolean;
  /**
   * Explicit ICE servers override for the WAN WebRTC direct carrier — mainly
   * for tests. Production leaves this unset: `createP0KernelRuntime` instead
   * feeds live `RTCIceServer`s from N3's qualified STUN source
   * (`core/adapters-network/src/webrtc_signal/stun::qualified_stun_urls()`,
   * surfaced over `/api/v2/client-transport/candidates`'s `stun_urls` field
   * and `DeviceSessionClient.currentStunUrls()`) into
   * `createHttpTransportSupervisor`'s `stunUrls` option, which takes over the
   * moment this override is left unset. No TURN — STUN/host candidates only,
   * matching this ticket's explicit TURN deferral (ticket #1865/#1866 review
   * "FIX 4").
   */
  readonly webrtcIceServers?: readonly RtcIceServerLike[];
}

export interface P0KernelDependencies {
  readonly backgroundSync?: P0BackgroundSync;
  readonly clock: ClockPort;
  readonly commands?: KernelRpcCommandDispatcher;
  /** #1815 onboarding-status coordinator: synchronous read of whether the browser currently
   * holds an established device session. Defaults to `() => false` when omitted, which keeps
   * `onboarding.status.v1` fail-safe (it still derives `needs-onboarding`/`needs-pairing`/
   * `bootstrap-required` correctly from the node alone — it just never reports `ready` on its
   * own, matching every caller that doesn't wire real session state). */
  readonly hasActiveSession?: () => boolean;
  readonly homeNode: P0HomeNodeClient;
  /** Pairing-plane home-node base URL (`resolveHomeNodeBaseUrl`'s result in
   * `createP0KernelRuntime`) — the SAME origin `DeviceSessionClient` targets for
   * `challenge`/`pair`/`session`. Reused (not re-derived) for the #1815 onboarding-status
   * coordinator's pre-session `claimedProbe`, so transport dialing, pairing, and the
   * onboarding-status probe all agree on one node (onboarding-pairing-handoff plan, Task 3). */
  readonly homeNodeBaseUrl: string;
  readonly identity: IdentityPort;
  readonly outboxDelivery?: OutboxDelivery;
  readonly ownerViews?: OwnerViewRepository;
  readonly stages: StageAssetPort & StageRegistryPort;
  readonly storage: P0StoragePort;
  readonly telemetry?: ClientTelemetryCollector;
  /** Production-owned HTTP recovery lifecycle; absent for compatibility with direct test composition. */
  readonly transportRecoveryCadenceMs?: number;
  readonly transportRefreshCadenceMs?: number;
  readonly transportSupervisor?: P0TransportSupervisor;
  readonly syncCadenceMs?: number;
  readonly syncDomains?: readonly P0SyncDomain[];
}

interface P0TransportSupervisor {
  close(): void | Promise<void>;
  reconcile(): Promise<void>;
  refreshCandidates(): Promise<void>;
}

export function createP0KernelRuntime(
  baseUrl: string,
  options: P0KernelRuntimeOptions = {},
): KernelHostRuntime {
  const protectedIdentity = new WebCryptoIdentity();
  const legacyAccessToken = createLegacyAccessTokenProvider();
  const stages = createBrowserStageCache(baseUrl);
  const storage = createDedicatedWorkerStoragePort({
    identity: protectedIdentity,
    stages,
    workerUrl: options.storageWorkerUrl ?? new URL("/storage.worker.js", baseUrl),
  });
  const identity = new StorageMirroredIdentity(protectedIdentity, storage);
  // Read the C3 stored candidate BEFORE constructing anything that needs a home-node
  // address — it now feeds BOTH planes the C3 rewire touches:
  //  - the transport supervisor below, via `resolveInitialHomeNode` (already correct pre-C5:
  //    it prepends the stored `lan-direct` candidate to what the manager races/dials);
  //  - `DeviceSessionClient` here, via `resolveHomeNodeBaseUrl` (the C5 fix — pre-C5 this
  //    client always used the raw `baseUrl` param, i.e. the document's own origin, which on
  //    the installed-from-cloud path is the static `client-node.economy1.cloud` distribution
  //    site. `challenge`/`pair`/`session` POSTed there and 404'd, silently defeating LAN
  //    pairing even after discovery had found and persisted the real node).
  // Both derivations start from the same `storedHomeNode` value, so once discovery has run,
  // transport dialing and pairing agree on the same node instead of diverging.
  const homeNodeCandidateStore = options.homeNodeCandidateStore ?? createBrowserHomeNodeCandidateStore();
  const storedHomeNode = homeNodeCandidateStore.read();
  const homeNodeBaseUrl = resolveHomeNodeBaseUrl({ origin: baseUrl, stored: storedHomeNode });
  const deviceSessions = new DeviceSessionClient({ baseUrl: homeNodeBaseUrl, identity });
  const accessToken = async () => deviceSessions.currentSession()?.sessionToken ?? await legacyAccessToken();
  const delegationCredential = async () => deviceSessions.currentSession()?.delegationCredential;
  const syncDomains = options.syncDomains === undefined
    ? ["conversation"]
    : options.syncDomains.map(({ domain }) => domain);
  const supervisor = createHttpTransportSupervisor({
    accessToken,
    baseUrl,
    delegationCredential,
    identity,
    refreshCandidates: async (signal) => {
      const refreshed = await deviceSessions.refreshTransportCandidates(signal);
      return [...refreshed.lan, refreshed.wan];
    },
    initialCandidates: httpCandidates(options.homeNodeCandidates ?? []),
    ...(storedHomeNode === undefined ? {} : { storedHomeNode }),
    ...(options.transportCandidateTimeoutMs === undefined
      ? {}
      : { candidateTimeoutMs: options.transportCandidateTimeoutMs }),
    ...(options.transportPriorityGraceMs === undefined
      ? {}
      : { priorityGraceMs: options.transportPriorityGraceMs }),
    ...(syncDomains === undefined ? {} : { syncDomains }),
    ...(options.webrtcIceServers === undefined ? {} : { webrtcIceServers: options.webrtcIceServers }),
    stunUrls: () => deviceSessions.currentStunUrls(),
    webrtcPromotionEnabled: options.webrtcPromotionEnabled ?? isWanWebRtcPromotionEnabled(),
  });
  const homeNode = supervisor.client;
  return composeP0KernelRuntime({
    clock: new BrowserClock(),
    commands: new ClientDeviceCommandDispatcher(deviceSessions),
    // #1815: reflects the SAME `DeviceSessionClient` instance the pairing gate drives
    // (`client-device.session.v1`/`client-device.pair.v1` above), so `onboarding.status.v1`
    // reports `ready` exactly when a session is actually established.
    hasActiveSession: () => deviceSessions.currentSession() !== undefined,
    homeNode,
    homeNodeBaseUrl,
    identity,
    ...(options.outboxRoutes === undefined
      ? { outboxDelivery: createClientSyncOutboxDelivery(homeNode, DEFAULT_CLIENT_SYNC_OUTBOX_ROUTES) }
      : { outboxDelivery: createClientSyncOutboxDelivery(homeNode, options.outboxRoutes) }),
    ownerViews: createHomeNodeOwnerViewRepository(homeNode),
    stages,
    storage,
    ...(options.backgroundSync === undefined ? {} : { backgroundSync: options.backgroundSync }),
    ...(options.telemetry === undefined ? {} : { telemetry: options.telemetry }),
    ...(options.syncCadenceMs === undefined ? {} : { syncCadenceMs: options.syncCadenceMs }),
    ...(options.syncDomains === undefined ? {} : { syncDomains: options.syncDomains }),
    ...(options.transportRecoveryCadenceMs === undefined
      ? {}
      : { transportRecoveryCadenceMs: options.transportRecoveryCadenceMs }),
    ...(options.transportRefreshCadenceMs === undefined
      ? {}
      : { transportRefreshCadenceMs: options.transportRefreshCadenceMs }),
    transportSupervisor: supervisor,
  });
}

export function createHttpTransportSupervisor(options: {
  readonly accessToken: () => Promise<string | undefined>;
  readonly baseUrl: string;
  readonly candidateTimeoutMs?: number;
  readonly delegationCredential: () => Promise<string | undefined>;
  readonly identity: IdentityPort;
  readonly initialCandidates: readonly HttpHomeNodeCandidate[];
  readonly priorityGraceMs?: number;
  readonly refreshCandidates: (
    signal: AbortSignal,
  ) => Promise<readonly HttpHomeNodeCandidate[]>;
  /** See `resolveInitialHomeNode`'s doc comment — the lan-direct-pairing plan's C3 stored
   * candidate, already read (synchronously, by the caller) from `HomeNodeCandidateStorePort`.
   * Omitted in every existing test in this file, which preserves their pre-C3 baseUrl-seeded
   * behavior exactly (`resolveInitialHomeNode` falls through to its origin-based branches). */
  readonly storedHomeNode?: StoredHomeNodeCandidate;
  readonly syncDomains?: readonly string[];
  /** See `P0KernelRuntimeOptions.webrtcIceServers`'s doc comment. Takes priority over
   * `stunUrls` below when provided — the explicit override this option has always been. */
  readonly webrtcIceServers?: readonly RtcIceServerLike[];
  /** See `P0KernelRuntimeOptions.webrtcPromotionEnabled`'s doc comment. */
  readonly webrtcPromotionEnabled?: boolean;
  /**
   * Live source of the node-advertised qualified STUN URLs (ticket
   * #1865/#1866 review "FIX 4"; `DeviceSessionClient.currentStunUrls()` in
   * production). Called fresh on every WebRTC promotion attempt — never
   * awaited, since it must stay synchronous for `webRtcPromotionFactory`'s
   * `createClient` to read it inline. Ignored entirely when
   * `webrtcIceServers` is explicitly provided.
   */
  readonly stunUrls?: () => readonly string[];
}): HttpTransportSupervisor {
  const baseUrl = new URL(options.baseUrl).origin;
  const initialCandidates: readonly HttpHomeNodeCandidate[] = [
    ...resolveInitialHomeNode({
      defaultDistributionOrigin: DEFAULT_CLIENT_NODE_ORIGIN,
      origin: baseUrl,
      stored: options.storedHomeNode,
    }),
    ...options.initialCandidates,
  ];
  const manager = createBrowserTransportManager({
    candidates: initialCandidates,
    ...(options.candidateTimeoutMs === undefined ? {} : { candidateTimeoutMs: options.candidateTimeoutMs }),
    ...(options.priorityGraceMs === undefined ? {} : { priorityGraceMs: options.priorityGraceMs }),
    lan: {
      client: {
        accessToken: options.accessToken,
        delegationCredential: options.delegationCredential,
        ...(options.syncDomains === undefined ? {} : { syncDomains: options.syncDomains }),
      },
    },
    wan: {
      accessToken: options.accessToken,
      delegationCredential: options.delegationCredential,
      ...(options.syncDomains === undefined ? {} : { syncDomains: options.syncDomains }),
    },
  });
  const getIceServers = (): readonly RtcIceServerLike[] => options.webrtcIceServers ??
    stunIceServers(options.stunUrls?.() ?? []);
  const webrtc = options.webrtcPromotionEnabled === true
    ? webRtcPromotionFactory(baseUrl, options.accessToken, options.delegationCredential, getIceServers)
    : undefined;
  return new HttpTransportSupervisor({
    identity: options.identity,
    initialCandidates,
    manager,
    refresh: async (_client, signal) => await options.refreshCandidates(signal),
    ...(webrtc === undefined ? {} : { webrtc }),
  });
}

/**
 * WAN WebRTC direct promotion (ticket #1865): builds the
 * `HttpTransportSupervisor` `webrtc` factory out of the real N4/N5 classes
 * — one `WebRtcPromotionController` driving one `WebRtcSignalingWsAdapter`
 * + `WebRtcHomeNodeClient` pair per managed client. Authenticated the same
 * way the HTTP/WS carriers already are: a bearer access token plus the
 * client-device delegation credential, mirroring `HttpWsHomeNodeClient`'s
 * header pair exactly (see `core/adapters-network/src/webrtc_signal/
 * auth.rs`, which requires both `authorization` and
 * `x-client-device-delegation` on the signaling WS's setup frame).
 *
 * `getIceServers` is called fresh on every promotion attempt rather than
 * captured once here (ticket #1865/#1866 review "FIX 4"): at the time this
 * factory itself runs (kernel/supervisor construction), no transport
 * discovery round trip has happened yet, so a value captured here would
 * always be the caller's static override (or empty). A real promotion
 * attempt only ever happens once the WAN HTTP/WS carrier is already
 * connected and stable, by which point at least one discovery round trip
 * (`DeviceSessionClient.currentStunUrls()`) has typically already run.
 */
function webRtcPromotionFactory(
  baseUrl: string,
  accessToken: () => Promise<string | undefined>,
  delegationCredential: () => Promise<string | undefined>,
  getIceServers: () => readonly RtcIceServerLike[],
): (client: ManagedHomeNodeClient) => WebRtcPromotionPort {
  return (client) => new WebRtcPromotionController({
    client,
    createClient: () => new WebRtcHomeNodeClient({
      iceServers: getIceServers(),
      setupHeaders: async () => {
        const headers: Record<string, string> = {};
        const token = await accessToken();
        if (token !== undefined) headers.authorization = `Bearer ${token}`;
        const delegation = await delegationCredential();
        if (delegation !== undefined) headers["x-client-device-delegation"] = delegation;
        return headers;
      },
      signaling: new WebRtcSignalingWsAdapter({ baseUrl }),
    }),
  });
}

/**
 * Turns the node-advertised qualified STUN URLs (ticket #1865/#1866 review
 * "FIX 4"; `ClientTransportDiscovery`'s `stunUrls`, ultimately N3's
 * `qualified_stun_urls()` on the node) into the `RtcIceServerLike` shape
 * `WebRtcHomeNodeClient` expects. One `RTCIceServer` entry carrying every
 * URL — browsers accept a `urls` array on a single entry, and there is no
 * per-URL credential to keep separate (STUN, unlike TURN, needs none).
 */
function stunIceServers(stunUrls: readonly string[]): readonly RtcIceServerLike[] {
  return stunUrls.length === 0 ? [] : [{ urls: [...stunUrls] }];
}

/**
 * WAN WebRTC direct promotion's rollout gate (ticket #1865): the `?webrtc=1`
 * URL query flag — the same dev-flag idiom `client/ui`'s onboarding gate
 * already uses for `?onboarding=1` (`app-shell.ts`'s
 * `#shouldShowOnboarding`); no other runtime feature-flag mechanism exists
 * in this composition root today. Absent the flag (the production
 * default), `createHttpTransportSupervisor` never builds a `webrtc` factory
 * at all, so `HttpTransportSupervisor` runs exactly the shipped HTTP/
 * WebTransport ladder — the identical code path as before this ticket.
 * Guarded for non-browser (`globalThis.location` undefined) environments —
 * e.g. this composition root's own Node-based test suite — where it
 * safely defaults to disabled.
 *
 * Exported (review follow-up on tickets #1865/#1866, finding C) so its
 * `?webrtc=1` parsing is directly unit-testable — `createP0KernelRuntime`
 * itself can't be exercised in this suite's Node test environment because
 * it constructs a real `Worker`-backed storage port, so this is the seam
 * that actually catches a broken/removed URL flag check.
 */
export function isWanWebRtcPromotionEnabled(): boolean {
  const search = (globalThis as { readonly location?: { readonly search?: string } }).location?.search ?? "";
  return /(?:^|[?&])webrtc=1(?:&|$)/u.test(search);
}

function httpCandidates(candidates: readonly HomeNodeCandidate[]): readonly HttpHomeNodeCandidate[] {
  return candidates.filter(isHttpHomeNodeCandidate);
}

/**
 * Boot-time home-node seeding decision (lan-direct-pairing plan, C3). Factored out as a
 * pure function — no `fetch`, no storage, no `URL` — so its truth table is directly
 * unit-testable without constructing a real transport supervisor. `createHttpTransportSupervisor`
 * is the sole caller; it prepends this function's result to `options.initialCandidates`
 * where the pre-C3 code unconditionally prepended a `wan-direct` bootstrap candidate built
 * from `baseUrl` alone.
 *
 * Three cases, in priority order:
 *
 * 1. `stored` is present (a previously-verified LAN candidate from
 *    `HomeNodeCandidateStorePort` — written when the onboarding discovery screen's
 *    `node-discovered` event fires, see `system/pwa/src/main.ts`) — seed IT as `lan-direct`.
 *    This is what makes every launch after a successful discovery skip discovery entirely
 *    and dial the real node directly, regardless of what origin the shell happens to be
 *    served from.
 * 2. Nothing is stored yet, and `origin` equals `defaultDistributionOrigin` (the cloud
 *    distribution origin the installed-from-cloud PWA is served from, e.g.
 *    `client-node.economy1.cloud`) — that origin is a static site, NEVER a node, so seed
 *    NOTHING. This is the bug this function fixes: before it, the distribution origin was
 *    unconditionally seeded as the `wan-direct` bootstrap candidate, so pairing dialed the
 *    static site and got a 404. The shell instead boots with no home-node candidate at all —
 *    `DiscoveryProbeService`/`DISCOVER_NODE_COMMAND` and the onboarding "scan" screen already
 *    handle a home-node-less boot correctly (see their own doc comments); they're what the
 *    person lands on next.
 * 3. Nothing is stored, and `origin` is anything else (a dev server, or a real node serving
 *    its own `system/pwa/dist` build directly at its own origin) — the origin itself IS the
 *    node. Seed it as the `wan-direct` bootstrap candidate exactly as before this ticket
 *    (`identityBinding: "bootstrap"`, matching the pre-C3 shape byte-for-byte).
 */
export function resolveInitialHomeNode(input: {
  readonly defaultDistributionOrigin: string;
  readonly origin: string;
  readonly stored: StoredHomeNodeCandidate | undefined;
}): readonly HttpHomeNodeCandidate[] {
  if (input.stored !== undefined) {
    return [{ address: input.stored.address, id: input.stored.id, path: "lan-direct" }];
  }
  if (input.origin === input.defaultDistributionOrigin) {
    return [];
  }
  return [{ address: input.origin, id: input.origin, identityBinding: "bootstrap", path: "wan-direct" }];
}

/**
 * Pairing-plane home-node base URL (lan-direct-pairing plan, C5 — the pairing-plane half
 * of the C3 rewire). `DeviceSessionClient` needs exactly one origin to POST
 * `challenge`/`pair`/`session` against; this is that single source of truth, and
 * `createP0KernelRuntime` is its only caller.
 *
 * Deliberately simpler than `resolveInitialHomeNode` above, which answers a different
 * question — what candidate SET should the *transport* supervisor race? — and can
 * legitimately answer "seed nothing" when the shell is served from the cloud distribution
 * origin with no stored candidate yet. A single-URL client has no "seed nothing" option, and
 * doesn't need one: pairing is never attempted before a home node exists (the shell shows
 * the discovery screen instead), so falling back to `origin` in that case is dead code, never
 * actually dialed. Hence no `defaultDistributionOrigin` branch here — unlike
 * `resolveInitialHomeNode`, the distribution-origin comparison doesn't change this
 * function's answer.
 *
 * Two cases, in priority order:
 *
 * 1. `stored` is present (a previously-verified LAN candidate, same source
 *    `resolveInitialHomeNode` reads) — target it directly. This is the fix: before it,
 *    `DeviceSessionClient` always used `origin` even after a successful discovery had
 *    persisted the real node's address, so pairing kept dialing the document's own origin
 *    (the cloud distribution site on the installed-from-cloud path) and 404'd.
 * 2. Nothing is stored — fall back to `origin` exactly as before this fix. Covers both the
 *    pre-discovery cloud-origin case (dead code, pairing isn't attempted) and the
 *    dev/served-from-node case, where `origin` already IS the node.
 */
export function resolveHomeNodeBaseUrl(input: {
  readonly origin: string;
  readonly stored: StoredHomeNodeCandidate | undefined;
}): string {
  return input.stored?.address ?? input.origin;
}

function isHttpHomeNodeCandidate(candidate: HomeNodeCandidate): candidate is HttpHomeNodeCandidate {
  return candidate.path === "lan-direct" || candidate.path === "wan-direct";
}

export function composeP0KernelRuntime(dependencies: P0KernelDependencies): KernelHostRuntime {
  const bus = new KernelEventBus();
  const detachTelemetry = dependencies.telemetry?.attach(bus) ?? (() => undefined);
  const queryRegistry = new LiveQueryRegistry();
  const liveQueries = new LiveQueryEngine(bus, queryRegistry);
  const durableEvents = new DurableEventDispatcher(dependencies.storage.repositories.events, bus, {
    now: () => dependencies.clock.now(),
  });
  const repositories = createEventEmittingStorageRepositories(dependencies.storage.repositories, durableEvents);
  const { outbox, settings } = repositories;
  const conversations = repositories.conversations ?? emptyConversationRepository();
  const notifications = repositories.notifications ?? emptyNotificationRepository();
  const id = new BrowserId();
  const conversationService = new ConversationService({ conversations, notifications, outbox }, {
    id: () => id.generate(),
  });
  const syncDomains = dependencies.syncDomains ?? [{
    domain: "conversation",
    strategy: createConversationLogSync(conversationService),
  }];
  const wallet = repositories.wallet === undefined
    ? undefined
    : new WalletMirrorService({
      homeNode: dependencies.homeNode,
      mirror: repositories.wallet,
      outbox: repositories.outbox,
    }, {
      staleAfterMs: 5 * 60 * 1_000,
    });
  const syncRegistry = new SyncStrategyRegistry();
  for (const configuration of syncDomains) {
    if (configuration.domain.trim() === "") throw new TypeError("Sync domain must not be empty");
    syncRegistry.register(configuration.domain, configuration.strategy);
  }
  const syncEngine = new SyncEngine({
    client: dependencies.homeNode,
    ledger: repositories.syncLedger,
    now: () => dependencies.clock.now(),
    registry: syncRegistry,
  });
  for (const configuration of syncDomains) syncEngine.startDomain(configuration.domain);
  liveQueries.register(createOutboxStatusQuery(outbox));
  liveQueries.register(createSettingsQuery(settings));
  liveQueries.register(createConversationListQuery(conversations));
  liveQueries.register(createConversationMessagesQuery(conversations));
  liveQueries.register(createNotificationsQuery(notifications));
  if (wallet !== undefined) {
    liveQueries.register(createWalletBalanceQuery(wallet));
    liveQueries.register(createPaymentHistoryQuery(wallet));
    liveQueries.register(createL402HistoryQuery(wallet));
  }
  if (repositories.agents !== undefined) {
    liveQueries.register(createAgentListQuery(repositories.agents));
    liveQueries.register(createAgentDetailQuery(repositories.agents));
  }
  if (repositories.revisionedSettings !== undefined) {
    liveQueries.register(createSettingsRevisionedListQuery(repositories.revisionedSettings));
    liveQueries.register(createSettingDetailQuery(repositories.revisionedSettings));
  }
  if (repositories.graphs !== undefined) {
    liveQueries.register(createGraphListQuery(repositories.graphs));
    liveQueries.register(createGraphDetailQuery(repositories.graphs));
  }
  // Degraded-mode surface (#1661): lets the shell subscribe to durable-storage
  // availability the same way it subscribes to transport state, so a browser that cannot
  // provide OPFS gets a specific, self-updating notice instead of a generic error screen.
  // `requiresDurableStorage: false` is what makes it observable precisely when storage is
  // NOT available — reading `storageState` is an in-memory field read.
  liveQueries.register({
    invalidateOn: ["client-storage.state-changed"],
    key: () => "client-storage",
    name: "client-storage.state.v1",
    requiresDurableStorage: false,
    run: async () => [clientStorageStatus()],
  });
  // #1815 interim LAN-discovery seam: the onboarding "search the node on the LAN" screen
  // reads `onboarding.discovery.v1` and drives it via `onboarding.discovery.{probe,promote}.v1`
  // (`docs/superpowers/plans/2026-07-25-client-node-lan-discovery.md`). The dialer reuses
  // `probeLanCandidate` directly — the exact function `createBrowserTransportManager` wires
  // for its own `lan-direct` branch — because `DiscoveryProbeService` only ever probes
  // `lan-direct` candidates (see its private `#dial`); no new probing logic is introduced
  // here. `requiresDurableStorage: false` (declared in the query itself): this must work
  // before the device is claimed/paired, so it is deliberately storage-free, matching
  // `client-storage.state.v1` above.
  const discoveryDialer: HomeNodeDialer = {
    dial: async (candidate, signal) => {
      if (!isHttpHomeNodeCandidate(candidate)) {
        throw new DomainError({
          code: "invalid_request",
          message: "Discovery dialer only supports lan-direct candidates",
        });
      }
      return await probeLanCandidate(candidate, undefined, signal);
    },
  };
  const discoveryProbe = new DiscoveryProbeService({
    dialer: discoveryDialer,
    onChange: discoveryInvalidation(bus, () => dependencies.clock.now()),
    refresh: new LanCandidateRefreshService({
      identity: dependencies.identity,
      now: () => dependencies.clock.now(),
    }),
  });
  liveQueries.register(createDiscoveryCandidatesQuery(discoveryProbe));
  // #1815 onboarding-status coordinator (Tasks B+C): serves `onboarding.status.v1`, which
  // `app-shell.ts`'s `#shouldShowOnboarding` now reads to mount onboarding by default for an
  // unclaimed node instead of only behind the `?onboarding=1` dev flag. `requiresDurableStorage:
  // false` for the same reason as the discovery seam above: it must resolve before the device
  // is claimed/paired, i.e. before the SQLite cache exists.
  // Pre-pairing there is no device session, so the managed `homeNode` transport can't
  // establish (its refresh path 401s before a session exists). `claimedProbe` reads the
  // node's public `claimed` signal via a transport-free plain fetch instead, targeting the
  // SAME `homeNodeBaseUrl` the pairing plane (`DeviceSessionClient`, above) already dials —
  // see `resolveHomeNodeBaseUrl`'s doc comment (onboarding-pairing-handoff plan, Task 3).
  const onboardingStatus = new OnboardingStatusService({
    hasActiveSession: dependencies.hasActiveSession ?? (() => false),
    claimedProbe: () => probeHomeNodeClaimed(dependencies.homeNodeBaseUrl),
  });
  liveQueries.register(createOnboardingStatusQuery(onboardingStatus));
  const ownerViews = new OwnerViewService(dependencies.ownerViews ?? emptyOwnerViewRepository());
  liveQueries.register(createHomeSummaryQuery(ownerViews));
  liveQueries.register(createPeerDirectoryQuery(ownerViews));
  liveQueries.register(createFeedTimelineQuery(ownerViews));
  liveQueries.register(createOperationalSummaryQuery(ownerViews));
  const scheduler = new KernelScheduler(dependencies.clock);
  const transportSupervisor = dependencies.transportSupervisor;
  let transportJobsCancelled = false;
  let transportSupervisorClose: Promise<void> | undefined;
  const stopTransport = (cleanupFailures: unknown[]): Promise<void> => {
    if (!transportJobsCancelled) {
      transportJobsCancelled = true;
      try {
        scheduler.cancel("transport.reconcile");
        scheduler.cancel("transport.candidate-refresh");
      } catch (error: unknown) {
        cleanupFailures.push(error);
      }
    }
    if (transportSupervisorClose !== undefined) return transportSupervisorClose;
    try {
      transportSupervisorClose = Promise.resolve(transportSupervisor?.close()).catch((error: unknown) => {
        cleanupFailures.push(error);
      });
    } catch (error: unknown) {
      cleanupFailures.push(error);
      transportSupervisorClose = Promise.resolve();
    }
    return transportSupervisorClose;
  };
  if (transportSupervisor !== undefined) {
    scheduler.register({
      cadenceMs: dependencies.transportRecoveryCadenceMs ?? 10_000,
      dueAt: dependencies.clock.now(),
      name: "transport.reconcile",
      run: async () => await transportSupervisor.reconcile(),
    });
    scheduler.register({
      cadenceMs: dependencies.transportRefreshCadenceMs ?? 300_000,
      dueAt: dependencies.clock.now(),
      name: "transport.candidate-refresh",
      run: async () => await transportSupervisor.refreshCandidates(),
    });
  }
  const outboxRunner = dependencies.outboxDelivery === undefined
    ? undefined
    : new OutboxRunner({
      bus,
      clock: dependencies.clock,
      delivery: dependencies.outboxDelivery,
      authoritativeDomains: ["wallet"],
      outbox: repositories.outbox,
      requireAtomicReplay: true,
      scheduler,
    });
  const identity = new IdentityStatusService({
    bus,
    clock: dependencies.clock,
    identity: dependencies.identity,
    liveQueries,
  });
  const stages = new StageRegistryService({
    assets: dependencies.stages,
    bus,
    clock: dependencies.clock,
    homeNode: dependencies.homeNode,
    liveQueries,
    repository: dependencies.stages,
    scheduler,
  });
  const authorization = new CapabilityAuthorization();
  const capabilities = new CapabilityDispatcher(dependencies.homeNode, authorization);
  let normalActivation: Promise<void> = Promise.resolve();
  let normalReadinessFailure: DomainError | undefined;
  const normalReadinessWaiters = new Set<{
    readonly reject: (error: DomainError) => void;
    readonly resolve: () => void;
  }>();
  let normalServicesInitialized = false;
  let normalServicesReady = false;
  let transportActivation: Promise<void> | undefined;
  let transportReadinessFailure: DomainError | undefined;
  const transportReadinessWaiters = new Set<{
    readonly reject: (error: DomainError) => void;
    readonly resolve: () => void;
  }>();
  let transportServicesReady = false;
  let shutdownPromise: Promise<void> | undefined;
  let shuttingDown = false;
  let storageAvailable = false;
  let storageInitialized = false;
  let storageState: StorageRecoveryState = { schemaVersion: 0, status: "starting" };
  let storageGeneration = 0;
  let unsubscribeStorageState: () => void = () => undefined;
  let unsubscribeTransport: () => void = () => undefined;
  let unsubscribeSyncTransport: () => void = () => undefined;
  let unsubscribeBackgroundSync: () => void = () => undefined;
  let syncRun: Promise<void> | undefined;

  const runSyncOnce = async (): Promise<void> => {
    if (syncEngine.activeDomains().length === 0) return;
    if (syncRun !== undefined) return await syncRun;
    syncRun = syncEngine.runOnce().then(() => undefined);
    try {
      await syncRun;
    } finally {
      syncRun = undefined;
    }
  };

  /**
   * Raised to callers of a surface that genuinely reads the durable, OPFS-backed SQLite
   * cache when that cache is not usable.
   *
   * The message names the actual reason (`StorageRecoveryState.message`, e.g. "Persistent
   * browser storage is unavailable" for `opfs-unavailable`) and `details.reason` carries
   * the machine-readable `StorageRecoveryCode`, so a stage or shell widget can say
   * specifically what is missing instead of rendering a generic failure. Surfaces that do
   * NOT read that cache never see this error at all — see `awaitTransportServicesReady`.
   */
  const recoveryRequiredError = (): DomainError => new DomainError({
    code: "conflict",
    details: {
      reason: storageState.status === "recovery-required" ? storageState.code : "not-ready",
      surface: "durable-storage",
    },
    message: storageState.status === "recovery-required"
      ? `Durable local storage is unavailable: ${storageState.message}`
      : "Durable local storage is not ready",
  });
  const transportUnavailableError = (): DomainError => new DomainError({
    code: "conflict",
    message: "Client kernel transport services are unavailable",
  });
  const rejectNormalReadiness = (error: DomainError): void => {
    for (const waiter of normalReadinessWaiters) waiter.reject(error);
    normalReadinessWaiters.clear();
  };
  const rejectTransportReadiness = (error: DomainError): void => {
    for (const waiter of transportReadinessWaiters) waiter.reject(error);
    transportReadinessWaiters.clear();
  };
  const markTransportServicesReady = (): void => {
    transportReadinessFailure = undefined;
    transportServicesReady = true;
    for (const waiter of transportReadinessWaiters) waiter.resolve();
    transportReadinessWaiters.clear();
  };
  /**
   * Readiness gate for every kernel surface that does NOT read the durable SQLite cache:
   * capability invocation (a home-node HTTP passthrough), stage bind/prepare (in-memory
   * catalog + CacheStorage assets), and the live queries whose definitions declare
   * `requiresDurableStorage: false`.
   *
   * Splitting this out from `awaitNormalServicesReady` is the #1661 fix. Previously every
   * one of those surfaces waited on the OPFS-backed cache it never touches, so a browser
   * that cannot provide OPFS at all — Safari Private Browsing, whose
   * `navigator.storage.getDirectory()` throws — could pair (#1623 already decoupled
   * `client-device.*`) but then dead-ended: `StageRegistryStore.start()` threw, the shell
   * caught it, and the user got a generic "Shell startup failed" with no working route.
   */
  const awaitTransportServicesReady = async (): Promise<void> => {
    if (transportServicesReady) return;
    if (transportReadinessFailure !== undefined) throw transportReadinessFailure;
    if (shuttingDown) throw transportUnavailableError();
    await new Promise<void>((resolve, reject) => {
      transportReadinessWaiters.add({ reject, resolve });
    });
  };
  const markNormalServicesReady = (): void => {
    normalReadinessFailure = undefined;
    normalServicesReady = true;
    for (const waiter of normalReadinessWaiters) waiter.resolve();
    normalReadinessWaiters.clear();
  };
  const awaitNormalServicesReady = async (): Promise<void> => {
    if (normalServicesReady) return;
    if (normalReadinessFailure !== undefined) throw normalReadinessFailure;
    if (shuttingDown || storageState.status === "recovery-required") throw recoveryRequiredError();
    await new Promise<void>((resolve, reject) => {
      normalReadinessWaiters.add({ reject, resolve });
    });
  };
  /**
   * Tier 1 activation — everything the kernel can run WITHOUT the durable SQLite cache:
   * the home-node event bridge (transport state + stage-catalog invalidation) and the
   * stage registry, which hydrates from CacheStorage and refreshes over HTTP.
   *
   * Runs unconditionally at startup, in parallel with (not after) storage initialization,
   * and is idempotent. Tier 2 (`activateNormalServices`) awaits it.
   */
  const activateTransportServices = (): Promise<void> => {
    if (transportActivation !== undefined) return transportActivation;
    const activation = (async () => {
      // Yield once before touching anything. This activation is kicked off synchronously
      // during composition (it must not wait on identity or storage), so without a yield
      // a caller that composes and immediately shuts down would still have attached a
      // home-node listener and hydrated the stage catalog. Yielding keeps tier 1 as
      // cancellable as tier 2 already was.
      await Promise.resolve();
      if (shuttingDown) return;
      unsubscribeTransport = dependencies.homeNode.subscribe((event) => {
        if (event.type === "transport.state-changed") {
          bus.publish({
            occurredAt: dependencies.clock.now(),
            payload: { state: event.state },
            type: "transport.state-changed",
          });
        } else if (event.domain === "stage.catalog-invalidated") {
          bus.publish({
            occurredAt: dependencies.clock.now(),
            payload: { reason: "app lifecycle changed" },
            type: "stage.catalog-invalidated",
          });
        }
      });
      // `initialize()` registers `stages.installed.v1` / `transport.state.v1` and hydrates
      // the catalog; it swallows a missing or unreadable offline copy internally, so a
      // browser with no persistent storage still reaches a usable catalog over HTTP.
      await stages.initialize();
      if (shuttingDown) {
        unsubscribeTransport();
        unsubscribeTransport = () => undefined;
        return;
      }
      markTransportServicesReady();
    })();
    transportActivation = activation.catch((error: unknown) => {
      transportReadinessFailure = transportUnavailableError();
      rejectTransportReadiness(transportReadinessFailure);
      throw error;
    }).catch(() => undefined);
    return activation;
  };
  const activateNormalServices = (): Promise<void> => {
    normalServicesReady = false;
    const activation = normalActivation.then(async () => {
      if (shuttingDown || !storageInitialized || !storageAvailable) return;
      await activateTransportServices();
      if (shuttingDown || !storageAvailable) return;
      await durableEvents.start();
      if (shuttingDown || !storageAvailable) return;
      await liveQueries.refreshAll();
      if (shuttingDown || !storageAvailable) return;
      if (!normalServicesInitialized) {
        if (syncEngine.activeDomains().length > 0) {
          unsubscribeSyncTransport = bus.subscribe("transport.state-changed", (event) => {
            if (event.payload.state.status === "connected") void runSyncOnce().catch(() => undefined);
          });
          scheduler.register({
            cadenceMs: dependencies.syncCadenceMs ?? 30_000,
            dueAt: dependencies.clock.now(),
            name: "sync.delta-replay",
            run: async () => await runSyncOnce(),
          });
          // The home-node event bridge is attached by the transport tier, which now
          // activates ahead of (and independently of) storage. A "connected" transition
          // can therefore land before this subscription exists, so catch up on the
          // current state instead of waiting a full cadence for the first replay.
          if (dependencies.homeNode.state.status === "connected") {
            void runSyncOnce().catch(() => undefined);
          }
        }
        if (shuttingDown || !storageAvailable) {
          // Only the durable-tier sync wiring is unwound here. The home-node event
          // bridge belongs to `activateTransportServices` and MUST survive a storage
          // outage — tearing it down would silently stop transport-state and
          // stage-catalog events the degraded shell still runs on.
          unsubscribeSyncTransport();
          unsubscribeSyncTransport = () => undefined;
          scheduler.cancel("sync.delta-replay");
          return;
        }
        normalServicesInitialized = true;
      }
      if (storageAvailable && !shuttingDown) markNormalServicesReady();
      if (storageAvailable && !shuttingDown) outboxRunner?.start();
    });
    normalActivation = activation.catch((error: unknown) => {
      normalReadinessFailure = new DomainError({
        code: "conflict",
        message: "Client normal services failed to activate",
      });
      rejectNormalReadiness(normalReadinessFailure);
      throw error;
    }).catch(() => undefined);
    return activation;
  };
  function clientStorageStatus(): ClientStorageStatusRow {
    return storageState.status === "recovery-required"
      ? { code: storageState.code, message: storageState.message, status: storageState.status }
      : { status: storageState.status };
  }
  const publishStorageState = (): void => {
    bus.publish({
      occurredAt: dependencies.clock.now(),
      payload: clientStorageStatus(),
      type: "client-storage.state-changed",
    });
  };
  const applyStorageState = (state: StorageRecoveryState): void => {
    const nextStorageGeneration = state.storageGeneration ?? 0;
    if (nextStorageGeneration !== storageGeneration) {
      storageGeneration = nextStorageGeneration;
      bus.resetDurableSequenceTracking();
    }
    storageState = state;
    storageAvailable = state.status === "ready";
    publishStorageState();
    if (!storageAvailable) {
      normalServicesReady = false;
      if (state.status === "recovery-required") rejectNormalReadiness(recoveryRequiredError());
      return;
    }
    if (storageInitialized && !shuttingDown) void activateNormalServices().catch(() => undefined);
  };
  unsubscribeStorageState = dependencies.storage.subscribeRecoveryState(applyStorageState);
  if (dependencies.backgroundSync !== undefined) {
    unsubscribeBackgroundSync = bus.subscribe("outbox.state-changed", (event) => {
      if (event.payload.intent.state === "pending") {
        void Promise.resolve(dependencies.backgroundSync?.request()).catch(() => undefined);
      }
    });
  }

  const awaitGateForQuery = async (name: QueryName): Promise<void> => {
    if (queryRegistry.requiresDurableStorage(name)) {
      await awaitNormalServicesReady();
      return;
    }
    await awaitTransportServicesReady();
  };

  const repositoryCommands = new SettingsCommandDispatcher(settings, dependencies.commands);
  const discoveryCommands = new DiscoveryCommandDispatcher(discoveryProbe);
  // Pre-pairing "is this typed-in address really a node" check (lan-direct-pairing plan).
  // Deliberately NOT `discoveryProbe`/`DiscoveryCommandDispatcher`: that flow re-proves a
  // candidate against an already-known home-node id. `discoverNodeByAddress` only checks
  // the address's public origin proof — no session, no expected node id — so it is reachable
  // under the same storage-free, pre-pairing conditions as the LAN-discovery commands below.
  const nodeDiscoveryCommands = new NodeDiscoveryCommandDispatcher({
    discover: (address) => discoverNodeByAddress(address),
  });
  const normalCommands: KernelRpcCommandDispatcher = {
    execute: async (name, payload) => {
      // Device pairing/session/migration/revocation commands are served entirely
      // through the backend (HTTP, via DeviceSessionClient) plus the
      // IndexedDB-backed WebCryptoIdentity store — they never read or write the
      // offline SQLite sync cache that `awaitNormalServicesReady()` guards.
      // Routing them through that gate anyway made device pairing — the
      // security-critical bootstrap operation every other capability depends
      // on — fail whenever the *unrelated* offline cache couldn't initialize
      // (quota pressure, corruption, or an engine-level OPFS fault), turning a
      // data-cache outage into a total inability to authenticate the device.
      if (name.startsWith("client-device.") && dependencies.commands !== undefined) {
        return await dependencies.commands.execute(name, payload);
      }
      // #1815: LAN-discovery probe/promote commands run *before* the device is
      // paired — the whole point is finding the node to pair with — so they must
      // be reachable under the exact same conditions as `client-device.*` above:
      // storage-free (the dialer + `LanCandidateRefreshService` never touch the
      // offline SQLite cache) and independent of `awaitNormalServicesReady()`.
      if (name === DISCOVERY_PROBE_COMMAND || name === DISCOVERY_PROMOTE_COMMAND) {
        return await discoveryCommands.execute(name, payload);
      }
      // Same pre-pairing reachability as the LAN-discovery commands above: this must work
      // before the device is claimed/paired, so it also bypasses `awaitNormalServicesReady()`.
      if (name === DISCOVER_NODE_COMMAND) {
        return await nodeDiscoveryCommands.execute(name, payload);
      }
      await awaitNormalServicesReady();
      return await repositoryCommands.execute(name, payload);
    },
  };
  const gatedCapabilities: KernelRpcCapabilityDispatcher = {
    // `CapabilityDispatcher` authorizes against the stage's declared `requires` and then
    // forwards to the home node over HTTP (`POST /api/v2/system/capabilities/invoke`).
    // It never reads or writes the OPFS-backed SQLite cache, so it waits on the transport
    // tier only. In-process providers registered via `CapabilityDispatcher.register` are
    // likewise expected to be storage-free; none are registered in this composition root.
    invoke: async (context, capability, payload) => {
      await awaitTransportServicesReady();
      return await capabilities.invoke(context, capability, payload);
    },
  };
  const gatedLiveQueries: KernelRpcLiveQueries = {
    // Per-query gating: a definition that declares `requiresDurableStorage: false` is
    // provably independent of the SQLite cache (in-memory state or a home-node round
    // trip), so it waits on the transport tier. Everything else — and any unregistered
    // name — keeps the durable-storage gate and fails with the specific
    // `recoveryRequiredError()` naming why the cache is unusable.
    query: async (name, params) => {
      await awaitGateForQuery(name);
      return await liveQueries.query(name, params);
    },
    subscribe: async (name, params, listener) => {
      await awaitGateForQuery(name);
      return liveQueries.subscribe(name, params, listener);
    },
    unsubscribe: (subscriptionId) => liveQueries.unsubscribe(subscriptionId),
  };
  const gatedStages: KernelRpcStageRegistry = {
    // Both read the stage registry's in-memory catalog; `prepareStage` additionally
    // resolves assets through `VerifiedStageCache`, which is CacheStorage + fetch. No
    // OPFS, so stages still mount in degraded mode.
    bindSession: async (appName, manifestSha256) => {
      await awaitTransportServicesReady();
      return stages.bindSession(appName, manifestSha256);
    },
    prepareStage: async (appName) => {
      await awaitTransportServicesReady();
      return await stages.prepareStage(appName);
    },
  };
  const rpc = new KernelRpcServer({
    authorization,
    bus,
    capabilities: gatedCapabilities,
    commands: new StorageRecoveryCommandDispatcher(
      dependencies.storage,
      normalCommands,
      async (action, state) => {
        storageInitialized = true;
        if (action === "reset" && state.status === "ready") bus.resetDurableSequenceTracking();
        applyStorageState(state);
        if (state.status !== "ready") return;
        await activateNormalServices();
        if (action === "reset" && normalServicesReady) {
          bus.publish({
            occurredAt: dependencies.clock.now(),
            payload: { action: "deleted", entityId: "local-client-storage", entityType: "client-storage" },
            type: "domain.entity-changed",
          });
        }
      },
      {
        begin: () => applyStorageState({
          schemaVersion: storageState.schemaVersion,
          ...(storageState.storageGeneration === undefined
            ? {}
            : { storageGeneration: storageState.storageGeneration }),
          status: "starting",
        }),
        runExclusive: async (operation) => await durableEvents.runExclusive(operation),
      },
    ),
    liveQueries: gatedLiveQueries,
    stages: gatedStages,
  });
  // The transport tier is started here, NOT inside `startup`, so it never waits on
  // `identity.start()` or `storage.initialize()` — on a browser with no OPFS the latter
  // resolves to `recovery-required`, and gating stage/capability/transport surfaces
  // behind it is exactly what dead-ended the shell before #1661.
  const transportStartup = activateTransportServices();
  const startup = identity.start().then(async () => {
    if (shuttingDown) return;
    const storageState = await dependencies.storage.initialize();
    if (shuttingDown) return;
    storageInitialized = true;
    applyStorageState(storageState);
    if (storageState.status === "ready") await activateNormalServices();
  });
  void startup.catch(() => {
    normalReadinessFailure = new DomainError({
      code: "conflict",
      message: "Client kernel failed to start",
    });
    rejectNormalReadiness(normalReadinessFailure);
  });

  return {
    attach: (transport) => rpc.attach(transport),
    wake: async () => {
      await outboxRunner?.runOnce();
      await runSyncOnce();
    },
    shutdown: () => {
      if (shutdownPromise !== undefined) return shutdownPromise;
      shuttingDown = true;
      normalServicesReady = false;
      transportServicesReady = false;
      rejectNormalReadiness(recoveryRequiredError());
      rejectTransportReadiness(transportUnavailableError());
      const cleanupFailures: unknown[] = [];
      const transportStopped = stopTransport(cleanupFailures);
      shutdownPromise = (async () => {
        let startupFailure: unknown;
        try {
          await transportStopped;
          await transportStartup;
          await startup;
          await normalActivation;
        } catch (error: unknown) {
          startupFailure = error;
        } finally {
          try {
            unsubscribeTransport();
          } catch (error: unknown) {
            cleanupFailures.push(error);
          }
          unsubscribeTransport = () => undefined;
          try {
            unsubscribeSyncTransport();
          } catch (error: unknown) {
            cleanupFailures.push(error);
          }
          unsubscribeSyncTransport = () => undefined;
          try {
            unsubscribeBackgroundSync();
          } catch (error: unknown) {
            cleanupFailures.push(error);
          }
          unsubscribeBackgroundSync = () => undefined;
          try {
            dependencies.backgroundSync?.close?.();
          } catch (error: unknown) {
            cleanupFailures.push(error);
          }
          try {
            unsubscribeStorageState();
          } catch (error: unknown) {
            cleanupFailures.push(error);
          }
          try {
            detachTelemetry();
          } catch (error: unknown) {
            cleanupFailures.push(error);
          }
          try {
            stages.shutdown();
          } catch (error: unknown) {
            cleanupFailures.push(error);
          }
          await stopTransport(cleanupFailures);
          try {
            dependencies.homeNode.close();
          } catch (error: unknown) {
            cleanupFailures.push(error);
          }
          try {
            outboxRunner?.stop();
          } catch (error: unknown) {
            cleanupFailures.push(error);
          }
          try {
            await dependencies.storage.close();
          } catch (error: unknown) {
            cleanupFailures.push(error);
          }
          try {
            await scheduler.shutdown();
          } catch (error: unknown) {
            cleanupFailures.push(error);
          }
        }
        if (startupFailure !== undefined) throw startupFailure;
        if (cleanupFailures[0] !== undefined) throw cleanupFailures[0];
      })();
      return shutdownPromise;
    },
  };
}

function emptyOwnerViewRepository(): OwnerViewRepository {
  return {
    listFeed: async () => [],
    listPeers: async () => [],
    readHomeSummary: async () => undefined,
    readOperationalSummary: async () => undefined,
  };
}

function emptyConversationRepository(): ConversationRepository {
  return {
    acknowledgeMessage: async () => ({ eventSequences: [], events: [] }),
    appendPendingMessage: () => ({ commit: async () => ({ eventSequences: [], events: [] }) }),
    applyDelta: () => ({ commit: async () => ({ eventSequences: [], events: [] }) }),
    failMessage: async () => ({ eventSequences: [], events: [] }),
    listConversations: async () => [],
    listMessages: async () => [],
  };
}

function emptyNotificationRepository(): NotificationRepository {
  return {
    applyDelta: () => ({ commit: async () => ({ eventSequences: [], events: [] }) }),
    listNotifications: async () => [],
    markRead: () => ({ commit: async () => ({ eventSequences: [], events: [] }) }),
  };
}

class ClientDeviceCommandDispatcher implements KernelRpcCommandDispatcher {
  #migrationChallenge: DevicePairingChallenge | undefined;

  constructor(private readonly sessions: DeviceSessionClient) {}

  async execute(
    name: string,
    payload: import("@econ-v1/domain").JsonValue,
  ): Promise<import("@econ-v1/domain").JsonValue> {
    try {
      return await this.#execute(name, payload);
    } catch (error: unknown) {
      if (error instanceof DeviceSessionRevokedError) {
        throw new DomainError({ code: "unauthorized", message: "Client device is revoked" });
      }
      throw error;
    }
  }

  async #execute(
    name: string,
    payload: import("@econ-v1/domain").JsonValue,
  ): Promise<import("@econ-v1/domain").JsonValue> {
    const input = record(payload);
    switch (name) {
      case "client-device.pair.v1":
        return json(await this.sessions.beginPairing({
          deviceName: optionalString(input, "deviceName") ?? this.sessions.defaultDeviceName(),
          deviceType: optionalString(input, "deviceType") ?? "pwa",
        }));
      case "client-device.session.v1":
      {
        const session = await this.sessions.establishSession(
          optionalString(input, "deviceName") ?? this.sessions.defaultDeviceName(),
          optionalString(input, "deviceType") ?? "pwa",
        );
        return json({
          delegationExpiresAt: session.delegationExpiresAt,
          deviceId: session.deviceId,
          scopes: session.scopes,
          sessionExpiresAt: session.sessionExpiresAt,
          status: "ready",
        });
      }
      case "client-device.migration-begin.v1": {
        const result = await this.sessions.beginPairing({
          deviceName: optionalString(input, "deviceName") ?? this.sessions.defaultDeviceName(),
          deviceType: optionalString(input, "deviceType") ?? "pwa",
          ownerPublicKey: string(input, "ownerPublicKey"),
          purpose: "migration",
        });
        this.#migrationChallenge = result.challenge;
        return json(result);
      }
      case "client-device.migration-complete.v1": {
        if (this.#migrationChallenge === undefined) throw new TypeError("Migration challenge is not active");
        const result = await this.sessions.completeMigration(this.#migrationChallenge, {
          deviceName: optionalString(input, "deviceName") ?? this.sessions.defaultDeviceName(),
          deviceType: optionalString(input, "deviceType") ?? "pwa",
          ownerPublicKey: string(input, "ownerPublicKey"),
          ownerSignature: string(input, "ownerSignature"),
          purpose: "migration",
        });
        this.#migrationChallenge = undefined;
        return json(result);
      }
      case "client-device.list.v1":
        return json(await this.sessions.listDevices(input.includeRevoked === true));
      case "client-device.revoke.v1":
        await this.sessions.revokeDevice(string(input, "id"));
        return null;
      case "client-device.rename.v1":
        await this.sessions.renameDevice(string(input, "id"), string(input, "deviceName"));
        return null;
      case "client-device.recover.v1":
        await this.sessions.recoverAfterRevocation();
        return null;
      default:
        throw new TypeError("Unknown client-device host command");
    }
  }
}

function record(value: import("@econ-v1/domain").JsonValue): Record<string, import("@econ-v1/domain").JsonValue> {
  if (typeof value !== "object" || value === null || Array.isArray(value)) throw new TypeError("Expected command payload object");
  return value;
}

function string(value: Record<string, import("@econ-v1/domain").JsonValue>, field: string): string {
  const candidate = value[field];
  if (typeof candidate !== "string" || candidate.trim() === "") throw new TypeError(`Expected ${field}`);
  return candidate;
}

function optionalString(
  value: Record<string, import("@econ-v1/domain").JsonValue>,
  field: string,
): string | undefined {
  const candidate = value[field];
  if (candidate === undefined) return undefined;
  if (typeof candidate !== "string" || candidate.trim() === "") throw new TypeError(`Expected ${field}`);
  return candidate;
}

function json(value: unknown): import("@econ-v1/domain").JsonValue {
  return JSON.parse(JSON.stringify(value)) as import("@econ-v1/domain").JsonValue;
}
